import React, {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    CSSProperties,
} from 'react';

type SizeMsg = { type?: string; frameHeight?: number; frameWidth?: number };

interface AdjustFrameProps {
    src: string;
    title?: string;
    style?: CSSProperties;
    className?: string;
    allowedOrigin?: string;

    // postMessage протокол (працює лише якщо дитина співпрацює)
    expectedMessageType?: string; // дефолт: 'IFRAME_SIZE'
    requestType?: string; // дефолт: 'REQUEST_IFRAME_SIZE'

    // початкові/межі
    initialHeight?: number; // дефолт: 600
    minHeight?: number; // дефолт: 0
    maxHeight?: number; // дефолт: Infinity
    debounceMs?: number; // дефолт: 80

    // фолбеки, якщо повідомлення не приходять
    fallbackMode?: 'viewport' | 'fixed' | 'none'; // дефолт: 'viewport'
    fallbackHeight?: number; // коли fallbackMode='fixed' (деф: 1000)
    viewportOffset?: number; // відняти верхній хедер, px (деф: 0)
    fallbackTimeoutMs?: number; // через скільки ввімкнути фолбек (деф: 800)

    // поведінка скролу у фреймі
    allowIframeScroll?: boolean; // дефолт: true (інакше контент може “різатись”)

    // колбеки
    onLoad?: () => void;
    onHeightChange?: (h: number) => void;
    onWidthChange?: (w: number) => void;
}

export const AdjustFrame: FC<AdjustFrameProps> = ({
    src,
    title,
    style,
    className,
    allowedOrigin,
    expectedMessageType = 'IFRAME_SIZE',
    requestType = 'REQUEST_IFRAME_SIZE',
    initialHeight = 600,
    minHeight = 0,
    maxHeight = Number.POSITIVE_INFINITY,
    debounceMs = 80,

    fallbackMode = 'viewport',
    fallbackHeight = 1000,
    viewportOffset = 0,
    fallbackTimeoutMs = 800,

    allowIframeScroll = false,

    onLoad,
    onHeightChange,
    onWidthChange,
}) => {
    const ref = useRef<HTMLIFrameElement | null>(null);
    const [height, setHeight] = useState(initialHeight);
    const timerRef = useRef<number | null>(null);
    const fallbackTimerRef = useRef<number | null>(null);
    const gotMessageRef = useRef(false); // чи прийшло хоч одне валідне повідомлення

    const clamp = useCallback(
        (v: number) => Math.max(minHeight, Math.min(maxHeight, v)),
        [minHeight, maxHeight],
    );

    const targetOrigin = useMemo(() => {
        try {
            return allowedOrigin ?? new URL(src).origin;
        } catch {
            return allowedOrigin ?? '*';
        }
    }, [allowedOrigin, src]);

    const computeViewportHeight = useCallback(
        () => clamp(Math.max(0, window.innerHeight - viewportOffset)),
        [clamp, viewportOffset],
    );

    // Скидаємо висоту при зміні src
    useEffect(() => {
        setHeight(initialHeight);
        gotMessageRef.current = false;
    }, [src, initialHeight]);

    // Фолбек: якщо за певний час не отримали ні одного повідомлення — ставимо висоту по в’юпорту/фіксовану
    useEffect(() => {
        if (fallbackTimerRef.current) {
            window.clearTimeout(fallbackTimerRef.current);
            fallbackTimerRef.current = null;
        }
        fallbackTimerRef.current = window.setTimeout(() => {
            if (gotMessageRef.current) return; // вже працює postMessage — не чіпаємо
            if (fallbackMode === 'viewport') setHeight(computeViewportHeight());
            else if (fallbackMode === 'fixed') setHeight(clamp(fallbackHeight));
        }, fallbackTimeoutMs);

        return () => {
            if (fallbackTimerRef.current) {
                window.clearTimeout(fallbackTimerRef.current);
                fallbackTimerRef.current = null;
            }
        };
    }, [
        src,
        fallbackMode,
        fallbackHeight,
        fallbackTimeoutMs,
        clamp,
        computeViewportHeight,
    ]);

    // Оновлюємо fallback-висоту при ресайзі вікна (якщо ми у режимі viewport або ще не прийшли меседжі)
    useEffect(() => {
        const onResize = () => {
            if (!gotMessageRef.current && fallbackMode === 'viewport') {
                setHeight(computeViewportHeight());
            }
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [fallbackMode, computeViewportHeight]);

    // Слухач повідомлень (якщо дитина щось шле)
    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (allowedOrigin && event.origin !== allowedOrigin) return;
            if (event.source !== ref.current?.contentWindow) return;

            const data = event.data as SizeMsg | undefined;
            if (!data) return;
            // Якщо дитина прислала type і він не збігається — ігноруємо
            if (data.type && data.type !== expectedMessageType) return;

            if (
                typeof data.frameHeight === 'number' ||
                typeof data.frameWidth === 'number'
            ) {
                gotMessageRef.current = true; // зафіксували, що протокол працює

                if (timerRef.current) {
                    window.clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
                timerRef.current = window.setTimeout(() => {
                    if (typeof data.frameHeight === 'number') {
                        const next = clamp(data.frameHeight);
                        setHeight(p => (p !== next ? next : p));
                        onHeightChange?.(next);
                    }
                    if (typeof data.frameWidth === 'number') {
                        onWidthChange?.(data.frameWidth);
                    }
                }, debounceMs);
            }
        };

        window.addEventListener('message', handler);
        return () => {
            window.removeEventListener('message', handler);
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [
        allowedOrigin,
        expectedMessageType,
        debounceMs,
        clamp,
        onHeightChange,
        onWidthChange,
    ]);

    // Handshake: кілька пінгів після onLoad — якщо дитина слухає, вона відповість
    const handleLoad = () => {
        onLoad?.();
        const cw = ref.current?.contentWindow;
        if (!cw) return;

        const ping = () => {
            try {
                cw.postMessage({ type: requestType }, targetOrigin);
            } catch {}
        };
        ping();
        setTimeout(ping, 150);
        setTimeout(ping, 400);
        setTimeout(ping, 1000);
    };

    return (
        <iframe
            ref={ref}
            key={src}
            src={src}
            title={title ?? src}
            onLoad={handleLoad}
            className={className}
            // якщо не прийшли повідомлення — даємо скрол всередині фрейму
            scrolling={allowIframeScroll ? 'auto' : 'no'}
            frameBorder={0}
            style={{
                width: '100%',
                border: 'none',
                overflow: allowIframeScroll ? 'auto' : 'hidden',
                height,
                ...style,
            }}
        />
    );
};
